import { z } from 'zod';
import {
  ChannelSchema,
  EmptyRecordSchema,
  IdSchema,
  SemverSchema,
  ThemeModeSchema,
} from './common';
import { CapabilitySchema } from './connector';

/**
 * Режим поставки кода виджета.
 *  remote  — код грузится с CDN во время работы (TMA, web, браузер);
 *  bundled — код собран в приложение (сторы), реестр статический.
 * Манифест и контракт сообщений при этом не меняются.
 */
export const WidgetDeliverySchema = z.enum(['remote', 'bundled']);
export type WidgetDelivery = z.infer<typeof WidgetDeliverySchema>;

export const WidgetManifestSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/, 'id: латиница в нижнем регистре и дефисы'),
    name: z.string().min(1),
    description: z.string().optional(),
    version: SemverSchema,
    delivery: WidgetDeliverySchema.default('remote'),
    /** HTML-точка входа (страница виджета) — обязательна для remote. */
    entryUrl: z.string().url().optional(),
    /** ESM-модуль виджета — для bundled-сборки и проверки целостности. */
    moduleUrl: z.string().url().optional(),
    /** sha256 бандла для проверки целостности перед загрузкой. */
    checksum: z.string().regex(/^[a-f0-9]{64}$/).optional(),
    /** Подпись манифеста и имя ключа (проверяет хост перед загрузкой). */
    signature: z.string().optional(),
    keyId: z.string().optional(),
    channels: z.array(ChannelSchema).min(1),
    minHostVersion: SemverSchema.optional(),
    capabilitiesRequired: z.array(CapabilitySchema).default([]),
    /** Схема конфига (JSON Schema) — бэкофис валидирует правки. */
    configSchema: EmptyRecordSchema.default({}),
    defaultConfig: EmptyRecordSchema.default({}),
    icon: z.string().optional(),
    sort: z.number().int().default(0),
  })
  .superRefine((manifest, ctx) => {
    if (manifest.delivery === 'remote' && !manifest.entryUrl) {
      ctx.addIssue({
        code: 'custom',
        path: ['entryUrl'],
        message: 'для режима remote обязателен entryUrl',
      });
    }
    if (manifest.delivery === 'bundled' && !manifest.moduleUrl) {
      ctx.addIssue({
        code: 'custom',
        path: ['moduleUrl'],
        message: 'для режима bundled обязателен moduleUrl',
      });
    }
  });
export type WidgetManifest = z.infer<typeof WidgetManifestSchema>;

/** Конкретное включение виджета для объекта с разрешённым режимом поставки. */
export const WidgetInstanceSchema = z.object({
  venueId: IdSchema,
  enabled: z.boolean(),
  delivery: WidgetDeliverySchema,
  config: EmptyRecordSchema.default({}),
  manifest: WidgetManifestSchema,
});
export type WidgetInstance = z.infer<typeof WidgetInstanceSchema>;

/** То, что хост получает от провайдера. */
export interface WidgetResolution {
  kind: WidgetDelivery;
  /** Страница виджета, которую открывает хост (iframe). */
  htmlUrl: string;
  moduleUrl?: string;
  version: string;
  /** true, если primary-провайдер не сработал и мы откатились на bundled. */
  fallback: boolean;
}

export interface WidgetProvider {
  readonly kind: WidgetDelivery;
  resolve(manifest: WidgetManifest): Promise<WidgetResolution>;
}

export const REGISTRY_DELIVERIES: Record<string, WidgetDelivery> = {
  tma: 'remote',
  web: 'remote',
  qr_table: 'remote',
  mobile: 'bundled',
};

export const HostInitSchema = z.object({
  widgetId: IdSchema,
  venueId: IdSchema,
  channel: ChannelSchema,
  guestToken: z.string().min(1),
  apiBaseUrl: z.string().url(),
  locale: z.enum(['ru', 'en']).default('ru'),
  theme: ThemeModeSchema.default('light'),
  config: EmptyRecordSchema.default({}),
  hostVersion: SemverSchema,
  /** Стол из QR-кода: в зале заказ без него не оформляется. */
  tableId: z.string().min(1).max(32).optional(),
});
export type HostInit = z.infer<typeof HostInitSchema>;
